import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigation } from 'context/NavigationContext';
import { BringzzPageContainer, BringzzIcon, BringzzText, BringzzDropdown } from "@bringzz/components";
import SearchFooter from './SearchFooter';
import { alphabets } from './constants';
import classNames from 'classnames';
import useRequest from 'hooks/useRequest';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

const PRODUCTS_PER_PAGE = 50;

const PrescriptionMedicinePage = () => {
    const { t } = useTranslation();
    const { backPress, navigate } = useNavigation();
    const { sendRequest, loading } = useRequest();
    const [products, setProducts] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sortBy, setSortBy] = useState('name_asc');
    const [alphabetFilter, setAlphabetFilter] = useState('#');
    const isLoadingMore = useRef(false);
    const observer = useRef();

    const sortOptions = [
        { value: 'name_asc', label: t('component.search.sort.name_asc') },
        { value: 'name_desc', label: t('component.search.sort.name_desc') },
        { value: 'price_asc', label: t('component.search.sort.price_asc') },
        { value: 'price_desc', label: t('component.search.sort.price_desc') },
        { value: 'pzn', label: 'PZN' },
    ];

    const lastProductElementRef = useCallback(node => {
        if (loading || isLoadingMore.current) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore && !isLoadingMore.current) {
                isLoadingMore.current = true;
                setPage(prevPage => prevPage + 1);
            }
        }, { threshold: 0.1 });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        const fetchRxProducts = async () => {
            try {
                const response = await sendRequest({
                    method: 'POST',
                    url: '/module/product/searchProducts',
                    data: {
                        query: alphabetFilter === '#' ? null : alphabetFilter,
                        page,
                        limit: PRODUCTS_PER_PAGE,
                        sortBy,
                        sortOrder: 'ASC',
                        rxOnly: true,
                        allProducts: alphabetFilter === '#'
                    }
                });

                if (response.status === 'success' && response.data) {
                    if (page === 1) {
                        setProducts(response.data);
                    } else {
                        setProducts(prev => [...prev, ...response.data]);
                    }
                    setHasMore(response.data.length === PRODUCTS_PER_PAGE);
                }
            } catch (error) {
                console.error('Error fetching Rx products:', error);
                toast.error(t('component.search.toast.fetch_failed') || 'Failed to fetch products');
            } finally {
                isLoadingMore.current = false;
            }
        };

        fetchRxProducts();
    }, [page, sortBy, alphabetFilter, sendRequest, t]);

    const handleSortChange = (newSortBy) => {
        if (newSortBy === sortBy) return;
        setSortBy(newSortBy);
        setPage(1);
        setProducts([]);
        setHasMore(true);
    };

    const handleAlphabetFilterChange = (char) => {
        if (char === alphabetFilter) return;
        setAlphabetFilter(char);
        setPage(1);
        setProducts([]);
        setHasMore(true);
    };

    return (
        <BringzzPageContainer
            className="bg-sand pb-32"
            authenticated={false}
            footerHover={true}
            header={{
                left: <BringzzIcon
                    size="22"
                    icon="IconChevronLeft"
                    folder="LineIcons"
                    className="cursor-pointer"
                    onClick={backPress}
                />,
                title: t('component.search.product_groups.prescription_medicine.short')
            }}
        >
            <div className='pt-2'>
                <div className='rounded-xl bg-white mx-2 py-2'>
                    <div className='flex items-center overflow-x-auto sticky top-0 bg-white z-10 py-2 border-b' style={{ paddingLeft: '12px', paddingRight: '12px' }}>
                        <BringzzText tag='h3' className='whitespace-nowrap mr-1'>{t('component.search.general.alphabet_filter')}</BringzzText>
                        <div className='flex space-x-3 sm:space-x-2 min-w-0'>
                            {alphabets.map((char, index) => {
                                const isSelected = char === alphabetFilter;
                                return (
                                    <div
                                        key={index}
                                        className={classNames('bg-sand p-2 rounded-full cursor-pointer flex-shrink-0',
                                            isSelected && "!bg-magic-lilac"
                                        )}
                                        onClick={() => handleAlphabetFilterChange(char)}
                                    >
                                        <BringzzText tag='h5'>{char}</BringzzText>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="p-4 border-b">
                        <BringzzDropdown
                            options={sortOptions}
                            value={sortBy}
                            onChange={handleSortChange}
                            placeholder={t('component.search.general.sort_by')}
                        />
                    </div>
                    {products.length > 0 ? (
                        products.map((product, index) => {
                            const isLast = index === products.length - 1;

                            return (
                                <div
                                    key={product.id || index}
                                    className="p-4 border-b flex items-center justify-between cursor-pointer"
                                    ref={isLast ? lastProductElementRef : null}
                                    onClick={() => navigate(`/product/details/${product.integrationId || product.pzn}`)}
                                >
                                    <BringzzText tag='h4' className="flex-grow">
                                        {product.name || `PZN: ${product.pzn}`} • {product.companyName || ''}
                                    </BringzzText>
                                    <BringzzIcon
                                        size="20"
                                        icon="IconChevronRight"
                                        folder="LineIcons"
                                        className="text-gray-400 flex-shrink-0 ml-2"
                                    />
                                </div>
                            );
                        })
                    ) : loading ? (
                        <div className="flex items-center justify-center p-8">
                            <BringzzIcon
                                size="32"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    ) : (
                        <div className="p-4">
                            <BringzzText tag='p'>
                                {t('component.search.empty.no_products')}
                            </BringzzText>
                        </div>
                    )}
                    {loading && products.length > 0 && (
                        <div className="flex items-center justify-center p-4">
                            <BringzzIcon
                                size="24"
                                icon="IconSpinner"
                                folder="LineIcons"
                                className="animate-spin text-magic-lilac"
                            />
                        </div>
                    )}
                </div>
                <SearchFooter />
            </div>
        </BringzzPageContainer>
    );
};

export default PrescriptionMedicinePage;
